import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowUpRight, Calendar, MapPin, Trophy, Crown, ChevronLeft, ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import DriverCard from "@/components/DriverCard";
import Countdown from "@/components/Countdown";
import { NextRaceBanner } from "@/pages/CalendarPage";
import { useSplits } from "@/lib/useSplits";
import { useSiteSettings } from "@/context/SiteSettingsContext";

export default function HomePage() {
  const { settings } = useSiteSettings();
  const cardsHidden = !!settings?.cards_hidden;
  const SPLITS = useSplits();
  const [races, setRaces] = useState([]);
  const [news, setNews] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [standings, setStandings] = useState([]);
  const [events, setEvents] = useState([]);
  const [championHistory, setChampionHistory] = useState([]);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    (async () => {
      const [r, n, d, t, s, c, h] = await Promise.all([
        api.get("/race-reports?published_only=true").then(x => x.data).catch(() => []),
        api.get("/news?published_only=true").then(x => x.data).catch(() => []),
        api.get("/drivers").then(x => x.data).catch(() => []),
        api.get("/teams").then(x => x.data).catch(() => []),
        api.get("/standings/drivers").then(x => x.data).catch(() => []),
        api.get("/calendar").then(x => x.data).catch(() => []),
        api.get("/champions/history").then(x => x.data).catch(() => []),
      ]);
      setRaces(r); setNews(n); setDrivers(d); setTeams(t); setStandings(s); setEvents(c); setChampionHistory(h);
    })();
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const nextBySplit = useMemo(() => {
    const out = {};
    for (const s of SPLITS) {
      out[s.key] = events
        .filter(e => e.split === s.key && !e.completed && new Date(`${e.date}T${e.time}:00`) > now)
        .sort((a, b) => `${a.date}T${a.time}` > `${b.date}T${b.time}` ? 1 : -1)[0];
    }
    return out;
  }, [events, now]);

  // Hero-Feed: News + Rennberichte gemerged, neueste 5
  const splitName = (k) => SPLITS.find(s => s.key === k)?.name || k;
  const splitColor = (k) => SPLITS.find(s => s.key === k)?.color || "#00E5FF";
  const heroFeed = useMemo(() => {
    const racesAsItems = (races || []).map(r => ({
      kind: "race",
      id: r.id,
      slug: r.slug,
      title: r.title,
      summary: r.summary,
      hero_image: r.hero_image,
      created_at: r.created_at,
      category: "Rennbericht",
      split: r.split,
      track: r.track,
      date: r.date,
      pinned: !!r.pinned,
    }));
    const newsAsItems = (news || []).map(n => ({
      kind: "news",
      id: n.id,
      slug: n.slug,
      title: n.title,
      summary: n.summary,
      hero_image: n.hero_image,
      created_at: n.created_at,
      category: n.category,
      pinned: !!n.pinned,
    }));
    const all = [...racesAsItems, ...newsAsItems]
      .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    const pinned = all.find(x => x.pinned);
    if (pinned) {
      const rest = all.filter(x => x.id !== pinned.id).slice(0, 4);
      return { pinned, items: [pinned, ...rest] };
    }
    return { pinned: null, items: all.slice(0, 5) };
  }, [races, news]);

  const feedItems = heroFeed.items;
  const isPinned = !!heroFeed.pinned;
  const [heroIdx, setHeroIdx] = useState(0);
  const [heroPaused, setHeroPaused] = useState(false);
  useEffect(() => {
    // Wenn gepinnt: nur Seiten-Slots rotieren (über Index 1..N).
    // Sonst: alle 5 Slots rotieren (Index 0..4).
    if (heroPaused) return;
    if (isPinned) {
      if (feedItems.length <= 2) return; // weniger als 1 + 1 → nichts zu rotieren
      const t = setInterval(() => setHeroIdx(i => (i + 1) % Math.max(1, feedItems.length - 1)), 12000);
      return () => clearInterval(t);
    }
    if (feedItems.length < 2) return;
    const t = setInterval(() => setHeroIdx(i => (i + 1) % feedItems.length), 12000);
    return () => clearInterval(t);
  }, [feedItems.length, heroPaused, isPinned]);

  let heroItem;
  let sideItems;
  if (isPinned) {
    heroItem = heroFeed.pinned;
    // Seiten-Slots: 4 Items aus feedItems[1..end], rotierend
    const pool = feedItems.slice(1);
    if (pool.length === 0) {
      sideItems = [];
    } else {
      const start = pool.length ? (heroIdx % pool.length) : 0;
      sideItems = Array.from({ length: Math.min(4, pool.length) }, (_, i) => pool[(start + i) % pool.length]);
    }
  } else {
    const heroSafeIdx = feedItems.length ? heroIdx % feedItems.length : 0;
    heroItem = feedItems[heroSafeIdx];
    sideItems = feedItems.filter((_, i) => i !== heroSafeIdx).slice(0, 4);
  }
  const linkFor = (it) => it.kind === "race" ? `/rennberichte/${it.slug}` : `/news/${it.slug}`;
  const eyebrowFor = (it) => {
    if (it.kind === "race") {
      return it.split ? `Rennbericht · ${splitName(it.split)}` : "Rennbericht";
    }
    return it.category || "News";
  };
  const eyebrowColor = (it) => (it.kind === "race" && it.split) ? splitColor(it.split) : "#00E5FF";

  const subRaces = races.slice(0, 3);
  const teamsById = Object.fromEntries(teams.map(t => [t.id, t]));
  const topDrivers = [...drivers].filter(d => d.is_active !== false).sort((a,b)=>b.ovr-a.ovr).slice(0,3);
  const champions = SPLITS
    .map(s => ({ split: s, driver: drivers.find(d => d.is_champion && !d.is_rookie && d.split === s.key) }))
    .filter(c => c.driver);

  const [champIdx, setChampIdx] = useState(0);
  const [champPaused, setChampPaused] = useState(false);
  useEffect(() => {
    if (champions.length < 2 || champPaused) return;
    const t = setInterval(() => setChampIdx(i => (i + 1) % champions.length), 7000);
    return () => clearInterval(t);
  }, [champions.length, champPaused]);
  const safeIdx = champions.length ? champIdx % champions.length : 0;
  const activeChamp = champions[safeIdx];

  // Hall of Champions (historische, abgeschlossene Saisons)
  const [hofIdx, setHofIdx] = useState(0);
  const [hofPaused, setHofPaused] = useState(false);
  useEffect(() => {
    if (championHistory.length < 2 || hofPaused) return;
    const t = setInterval(() => setHofIdx(i => (i + 1) % championHistory.length), 15000);
    return () => clearInterval(t);
  }, [championHistory.length, hofPaused]);
  const hofSafeIdx = championHistory.length ? hofIdx % championHistory.length : 0;
  const activeHof = championHistory[hofSafeIdx];

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
      {settings.countdown_enabled && settings.countdown_target && (
        <div className="mb-8">
          <Countdown
            title={settings.countdown_title}
            target={settings.countdown_target}
            url={settings.countdown_url}
            themeColor={settings.theme_color || "#00E5FF"}
          />
        </div>
      )}
      {/* HERO */}
      <section
        className="grid md:grid-cols-12 gap-6"
        data-testid="home-hero-section"
        onMouseEnter={() => setHeroPaused(true)}
        onMouseLeave={() => setHeroPaused(false)}
      >
        {/* LARGE LEFT */}
        <div className="md:col-span-8 relative min-h-[60vh] border border-white/10 overflow-hidden group flex flex-col justify-end">
          {heroItem?.hero_image && (
            <img
              key={heroItem.id}
              src={heroItem.hero_image}
              alt={heroItem.title}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 animate-fade-in"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/70 to-transparent z-10" />
          <div className="relative z-20 p-8 md:p-12">
            <div className="h-1 w-16 bg-[#00E5FF] mb-5" />
            <div className="eyebrow flex items-center gap-2" style={{ color: heroItem ? eyebrowColor(heroItem) : "#00E5FF" }}>
              {heroItem && eyebrowFor(heroItem)}
              {isPinned && (
                <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-current opacity-90">Angepinnt</span>
              )}
            </div>
            <h1
              key={`title-${heroItem?.id}`}
              className="font-display font-black italic uppercase tracking-normal text-4xl sm:text-6xl leading-none mt-3 max-w-3xl animate-fade-in"
              style={{ textShadow: "0 4px 20px rgba(0,0,0,0.8)" }}
            >
              {heroItem?.title || "Bald geht es los"}
            </h1>
            <p className="text-neutral-200 mt-4 max-w-2xl line-clamp-3">
              {heroItem?.summary || "Die ersten Rennberichte erscheinen in Kürze."}
            </p>
            {heroItem && (
              <div className="flex flex-wrap items-center gap-4 md:gap-6 mt-6 text-sm text-neutral-300">
                {heroItem.kind === "race" && heroItem.date && (
                  <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#00E5FF]" />{heroItem.date}</span>
                )}
                {heroItem.kind === "race" && heroItem.track && (
                  <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#00E5FF]" />{heroItem.track}</span>
                )}
                {heroItem.kind === "news" && heroItem.created_at && (
                  <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#00E5FF]" />{new Date(heroItem.created_at).toLocaleDateString("de-DE")}</span>
                )}
                <Link to={linkFor(heroItem)} className="btn-primary chamfer-sm" data-testid="hero-read-more">
                  {heroItem.kind === "race" ? "Bericht lesen" : "Artikel lesen"} <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </div>
          {/* Rotation Indicator Dots — nur wenn nicht gepinnt (sonst rotieren nur Side-Cards) */}
          {!isPinned && feedItems.length > 1 && (
            <div className="absolute bottom-4 left-8 z-20 flex items-center gap-1.5" data-testid="hero-dots">
              {feedItems.map((item, i) => (
                <button
                  key={item.id || `${item.type}-${i}`}
                  type="button"
                  onClick={() => setHeroIdx(i)}
                  className="h-1 transition-all"
                  style={{
                    width: i === (heroIdx % feedItems.length) ? 28 : 12,
                    background: i === (heroIdx % feedItems.length) ? "#00E5FF" : "rgba(255,255,255,0.3)",
                  }}
                  aria-label={`Bild ${i + 1}`}
                  data-testid={`hero-dot-${i}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* SIDE LIST (4 thumbnails) */}
        <div className="md:col-span-4 grid grid-cols-1 gap-3">
          {sideItems.map((it) => (
            <Link
              to={linkFor(it)}
              key={it.id}
              onClick={(e) => {
                // Klick auf eine Side-Card: bei nicht-gepinnter Rotation setze sie als Hero.
                // Bei gepinntem Modus erlaube Navigation (preventDefault entfällt).
                if (isPinned) return;
                const idx = feedItems.findIndex(x => x.id === it.id);
                if (idx >= 0) { e.preventDefault(); setHeroIdx(idx); }
              }}
              className="relative border border-white/10 overflow-hidden group flex items-stretch h-[calc((60vh-1.5rem)/4)] min-h-[88px] hover:border-[#00E5FF]/60 transition-colors"
              data-testid={`hero-side-${it.id}`}
            >
              {it.hero_image && (
                <img
                  src={it.hero_image}
                  alt={it.title}
                  className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-80 transition-opacity"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F19] via-[#0B0F19]/80 to-transparent" />
              <div className="relative z-10 p-3 flex flex-col justify-center w-full">
                <div className="text-[10px] uppercase tracking-widest truncate" style={{ color: eyebrowColor(it) }}>
                  {eyebrowFor(it)}
                </div>
                <div className="font-display font-black italic uppercase text-sm md:text-base mt-1 leading-tight line-clamp-2 group-hover:text-[#00E5FF] transition-colors">
                  {it.title}
                </div>
                <div className="text-[10px] text-neutral-500 mt-1">
                  {it.created_at && new Date(it.created_at).toLocaleDateString("de-DE")}
                </div>
              </div>
            </Link>
          ))}
          {feedItems.length === 0 && (
            <div className="card-surface p-6 text-neutral-400 text-sm">Noch keine Inhalte vorhanden.</div>
          )}
        </div>
      </section>

      {/* NEXT RACES PER SPLIT */}
      {(nextBySplit.champions || nextBySplit.elite) && (
        <section className="mt-16" data-testid="home-next-races-section">
          <SectionHeader title="Nächste Rennen" link="/kalender" linkLabel="Kompletter Kalender" />
          <div className="grid md:grid-cols-2 gap-6 mt-8">
            {SPLITS.map(s => nextBySplit[s.key] ? (
              <NextRaceBanner key={s.key} event={nextBySplit[s.key]} split={s} now={now} compact />
            ) : (
              <div key={s.key} className="card-surface p-6 flex flex-col justify-center" data-testid={`home-no-next-${s.key}`}>
                <div className="eyebrow" style={{ color: s.color }}>{s.name}</div>
                <div className="font-display font-black italic uppercase text-2xl mt-2">Kein Rennen geplant</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CURRENT CHAMPIONS (slider, one per split) */}
      {activeChamp && (
        <section className="mt-16" data-testid="home-champion-section">
          <div
            className="relative border border-[#A855F7]/30 overflow-hidden bg-gradient-to-br from-[#1B1230] via-[#0B0F19] to-[#0B0F19]"
            onMouseEnter={() => setChampPaused(true)}
            onMouseLeave={() => setChampPaused(false)}
            onFocusCapture={() => setChampPaused(true)}
            onBlurCapture={() => setChampPaused(false)}
          >
            {/* Purple glow */}
            <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full pointer-events-none"
                 style={{ background: "radial-gradient(closest-side, rgba(168,85,247,0.35), transparent)" }} />
            <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full pointer-events-none"
                 style={{ background: "radial-gradient(closest-side, rgba(168,85,247,0.18), transparent)" }} />

            <AnimatePresence mode="wait">
              <motion.div
                key={activeChamp.driver.id}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.45, ease: "easeOut" }}
                className="relative grid md:grid-cols-12 gap-8 p-6 md:p-10 items-center"
                data-testid={`home-champion-slide-${activeChamp.split.key}`}
              >
                {/* Card */}
                <div className="md:col-span-5 lg:col-span-4 flex justify-center md:justify-start">
                  <DriverCard
                    driver={activeChamp.driver}
                    team={teamsById[activeChamp.driver.team_id]}
                    teamsById={teamsById}
                    testId={`home-champion-card-${activeChamp.split.key}`}
                    hidden={cardsHidden}
                  />
                </div>

                {/* Text */}
                <div className="md:col-span-7 lg:col-span-8">
                  <div className="flex items-center gap-2">
                    <Crown className="w-4 h-4 text-[#E9D5FF]" strokeWidth={2.5} />
                    <span className="eyebrow text-[#E9D5FF]">Aktueller Champion</span>
                    <span className="text-neutral-500 text-xs">·</span>
                    <span className="eyebrow" style={{ color: activeChamp.split.color }}>{activeChamp.split.name}</span>
                  </div>
                  <div className="h-1 w-16 mt-4" style={{ background: "#A855F7" }} />
                  <h2 className="font-display font-black italic uppercase tracking-normal text-4xl sm:text-5xl lg:text-6xl leading-none mt-4"
                      style={{ textShadow: "0 4px 24px rgba(168,85,247,0.45)" }}>
                    {activeChamp.driver.name}
                  </h2>
                  <div className="flex flex-wrap items-center gap-3 mt-4 text-sm text-neutral-300">
                    {teamsById[activeChamp.driver.team_id] && (
                      <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/10">
                        {teamsById[activeChamp.driver.team_id].badge_url && (
                          <img src={teamsById[activeChamp.driver.team_id].badge_url} alt="" className="w-5 h-5 object-contain" />
                        )}
                        <span className="font-bold tracking-widest text-xs uppercase" style={{ color: teamsById[activeChamp.driver.team_id].color }}>
                          {teamsById[activeChamp.driver.team_id].name}
                        </span>
                      </span>
                    )}
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#A855F7]/40 bg-[#A855F7]/10">
                      <span className="text-[10px] uppercase tracking-widest text-[#E9D5FF]">OVR</span>
                      <span className="font-display font-black italic text-[#F3D9FF]">{activeChamp.driver.ovr}</span>
                    </span>
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/10">
                      <Trophy className="w-3.5 h-3.5 text-[#A855F7]" />
                      <span className="font-display font-black italic">{activeChamp.driver.champion_stats?.wins ?? activeChamp.driver.wins ?? activeChamp.driver.career_auto?.wins ?? 0}</span>
                      <span className="text-[10px] uppercase tracking-widest text-neutral-400">Siege</span>
                    </span>
                    {activeChamp.driver.champion_season && (
                      <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/10">
                        <span className="text-[10px] uppercase tracking-widest text-neutral-400">Saison</span>
                        <span className="font-display font-black italic">{activeChamp.driver.champion_season}</span>
                      </span>
                    )}
                  </div>
                  <p className="text-neutral-300 mt-6 max-w-2xl">
                    {activeChamp.driver.bio || `Saisonsieger der ${activeChamp.split.name}. ${activeChamp.driver.name} hat sich an die Spitze unserer Liga gefahren.`}
                  </p>
                  <div className="mt-6 flex flex-wrap items-center gap-3">
                    <Link to="/fahrer" className="btn-primary chamfer-sm" data-testid={`champion-cta-drivers-${activeChamp.split.key}`}
                          style={{ background: "#A855F7", color: "#fff" }}>
                      Champion-Karte ansehen <ArrowUpRight className="w-4 h-4" />
                    </Link>
                    <Link to="/wertung" className="btn-ghost text-xs" data-testid="champion-cta-standings">
                      Komplette Wertung
                    </Link>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Slider Controls — nur sichtbar wenn mehr als 1 Champion */}
            {champions.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => setChampIdx((safeIdx - 1 + champions.length) % champions.length)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 z-30 w-10 h-10 flex items-center justify-center border border-white/10 bg-black/40 hover:bg-[#A855F7]/30 transition-colors"
                  data-testid="champion-slider-prev"
                  aria-label="Vorheriger Champion"
                >
                  <ChevronLeft className="w-5 h-5 text-white" />
                </button>
                <button
                  type="button"
                  onClick={() => setChampIdx((safeIdx + 1) % champions.length)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 z-30 w-10 h-10 flex items-center justify-center border border-white/10 bg-black/40 hover:bg-[#A855F7]/30 transition-colors"
                  data-testid="champion-slider-next"
                  aria-label="Nächster Champion"
                >
                  <ChevronRight className="w-5 h-5 text-white" />
                </button>

                {/* Dots */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2" data-testid="champion-slider-dots">
                  {champions.map((c, i) => (
                    <button
                      key={c.driver.id}
                      type="button"
                      onClick={() => setChampIdx(i)}
                      className={`h-1.5 transition-all ${i === safeIdx ? "w-8 bg-[#A855F7]" : "w-3 bg-white/30 hover:bg-white/60"}`}
                      data-testid={`champion-slider-dot-${c.split.key}`}
                      aria-label={`Champion ${c.split.name}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* DRIVERS TEASER */}
      {topDrivers.length > 0 && (
        <section className="mt-24" data-testid="home-drivers-section">
          <SectionHeader title="Top Fahrer" link="/fahrer" linkLabel="Alle Karten" />

          <div className="relative mt-10">
            <div className={`grid md:grid-cols-3 gap-8 ${settings?.card_reveal_enabled ? "pointer-events-none select-none" : ""}`}>
              {topDrivers.map((d) => (
                <DriverCard key={d.id} driver={d} team={teamsById[d.team_id]} teamsById={teamsById} testId={`home-card-${d.id}`} hidden={cardsHidden} />
              ))}
            </div>

            {settings?.card_reveal_enabled && (
              <div
                className="absolute inset-0 flex items-center justify-center px-4"
                style={{
                  background: "radial-gradient(ellipse at center, rgba(11,15,25,0.55) 0%, rgba(11,15,25,0.85) 70%)",
                  backdropFilter: "blur(2px)",
                  WebkitBackdropFilter: "blur(2px)",
                }}
              >
                <Link
                  to="/karten-reveal"
                  data-testid="home-card-reveal-cta"
                  className="group relative block overflow-hidden chamfer border-2 border-cyan-400/60 hover:border-cyan-300 transition-all hover:scale-[1.03]"
                  style={{
                    background: "linear-gradient(95deg, rgba(0,229,255,0.20) 0%, rgba(0,229,255,0.10) 40%, rgba(255,215,0,0.14) 100%)",
                    boxShadow: "0 0 80px rgba(0,229,255,0.30), 0 24px 60px rgba(0,0,0,0.7)",
                    maxWidth: "min(820px, 100%)",
                    width: "100%",
                  }}
                >
                  <div
                    className="absolute inset-0 opacity-20 pointer-events-none"
                    style={{ backgroundImage: "repeating-linear-gradient(45deg, #00E5FF 0 2px, transparent 2px 14px)" }}
                  />
                  <div className="relative flex items-center gap-6 px-7 py-7 md:px-12 md:py-10 flex-wrap">
                    <div
                      className="shrink-0 flex items-center justify-center font-display font-black italic text-cyan-300"
                      style={{
                        width: 96, height: 96,
                        background: "rgba(0,229,255,0.16)",
                        border: "1px solid rgba(0,229,255,0.5)",
                        fontSize: 68, lineHeight: 1,
                        clipPath: "polygon(15% 0, 100% 0, 100% 85%, 85% 100%, 0 100%, 0 15%)",
                      }}
                      aria-hidden="true"
                    >?</div>
                    <div className="flex-1 min-w-[220px]">
                      <div className="eyebrow text-cyan-300 text-sm">Launch-Event</div>
                      <div className="font-display font-black italic uppercase tracking-tight text-3xl md:text-5xl leading-none mt-2">
                        Karten-Reveal
                      </div>
                    </div>
                    <span
                      className="ml-auto inline-flex items-center gap-2 px-7 py-4 bg-cyan-300 text-black font-bold uppercase tracking-widest text-sm md:text-base group-hover:bg-white transition-colors"
                      aria-hidden="true"
                    >
                      Pack öffnen →
                    </span>
                  </div>
                </Link>
              </div>
            )}
          </div>
        </section>
      )}

      {/* HALL OF CHAMPIONS — abgeschlossene Saisons */}
      {championHistory.length > 0 && (
        <section className="mt-16" data-testid="home-hof-section">
          <SectionHeader title="Hall of Champions" link="/wertung" linkLabel="Komplette Wertung" />
          <div
            className="mt-8 relative border border-amber-400/30 overflow-hidden"
            style={{ background: "linear-gradient(135deg, rgba(255,215,0,0.06), #0B0F19 60%)" }}
            onMouseEnter={() => setHofPaused(true)}
            onMouseLeave={() => setHofPaused(false)}
            data-testid="home-hof-slider"
          >
            {/* Trophy Glow */}
            <div
              aria-hidden
              className="absolute -right-16 -top-16 w-72 h-72 rounded-full opacity-[0.08] blur-3xl"
              style={{ background: "#FFD700" }}
            />
            <AnimatePresence mode="wait">
              <motion.div
                key={activeHof?.season_id}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.45, ease: "easeOut" }}
                className="relative p-6 md:p-8"
                data-testid={`home-hof-slide-${activeHof?.season_id}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Trophy className="w-4 h-4 text-amber-300" strokeWidth={2.5} />
                  <span className="eyebrow text-amber-300/90">
                    {activeHof?.season_name}
                    {activeHof?.year ? ` · ${activeHof.year}` : ""}
                    {activeHof?.game ? ` · ${activeHof.game}` : ""}
                  </span>
                  <span className="text-neutral-500 text-xs">·</span>
                  <span className="eyebrow" style={{ color: SPLITS.find(s => s.key === activeHof?.split)?.color || "#A5F3FC" }}>
                    {splitName(activeHof?.split)}
                  </span>
                </div>
                <div className="h-1 w-16 mt-3 mb-5" style={{ background: "#FFD700" }} />

                <div className="space-y-4">
                  {/* DRIVER CHAMPION */}
                  {activeHof?.driver && (
                    <div className="border border-amber-400/20 bg-[#0B0F19]/60 p-4 backdrop-blur-sm" data-testid="hof-driver-card">
                      <div className="flex items-start gap-3">
                        <div className="shrink-0 w-16 h-16 border border-amber-400/30 bg-[#11151E] overflow-hidden flex items-center justify-center">
                          {activeHof.driver.photo_url ? (
                            <img src={activeHof.driver.photo_url} alt={activeHof.driver.driver_name} className="w-full h-full object-cover" draggable={false} />
                          ) : (
                            <div className="w-full h-full" style={{ background: activeHof.driver.team_color || "#1F2937" }} />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] uppercase tracking-widest text-amber-300/80">Fahrer</div>
                          <div className="font-display font-black italic uppercase tracking-tight text-xl md:text-2xl leading-tight mt-1 truncate">
                            {activeHof.driver.driver_name}
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-xs">
                            {activeHof.driver.country_code && (
                              <img src={`https://flagcdn.com/w40/${activeHof.driver.country_code.toLowerCase()}.png`} alt="" className="w-5 h-3.5 object-cover border border-white/10" draggable={false} />
                            )}
                            {activeHof.driver.number > 0 && <span className="text-neutral-500">#{activeHof.driver.number}</span>}
                            {activeHof.driver.team_name && (
                              <span className="font-bold truncate" style={{ color: activeHof.driver.team_color }}>{activeHof.driver.team_name}</span>
                            )}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-display font-black italic text-2xl leading-none" style={{ color: "#FFD700" }}>{activeHof.driver.points ?? 0}</div>
                          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">Punkte</div>
                        </div>
                      </div>
                      {(activeHof.driver.wins != null || activeHof.driver.podiums != null) && (
                        <div className="flex items-center gap-3 mt-3 text-[10px] uppercase tracking-widest text-neutral-400 border-t border-white/5 pt-2">
                          <span><span className="text-white font-bold">{activeHof.driver.wins || 0}</span> Siege</span>
                          <span>·</span>
                          <span><span className="text-white font-bold">{activeHof.driver.podiums || 0}</span> Podien</span>
                          <span>·</span>
                          <span><span className="text-white font-bold">{activeHof.driver.poles || 0}</span> Poles</span>
                          <span>·</span>
                          <span><span className="text-white font-bold">{activeHof.driver.fastest_laps || 0}</span> FL</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* TEAM CHAMPION (centered, with 2 team drivers below) */}
                  {activeHof?.team && (
                    <div
                      className="relative border border-amber-400/30 bg-[#0B0F19]/70 p-6 md:p-8 backdrop-blur-sm overflow-hidden"
                      style={{
                        background: `radial-gradient(ellipse at top, ${activeHof.team.color || "#1F2937"}22, #0B0F19 70%)`,
                      }}
                      data-testid="hof-team-card"
                    >
                      {/* Top right points */}
                      <div className="absolute top-4 right-5 text-right">
                        <div className="font-display font-black italic text-3xl md:text-4xl leading-none" style={{ color: "#FFD700" }}>{activeHof.team.points ?? 0}</div>
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">Punkte</div>
                      </div>

                      {/* Centered team identity */}
                      <div className="flex flex-col items-center text-center pt-2">
                        <div className="text-[10px] uppercase tracking-widest text-amber-300/80">Konstrukteur</div>
                        <div className="mt-3 w-24 h-24 md:w-28 md:h-28 border border-amber-400/40 bg-[#11151E] overflow-hidden flex items-center justify-center">
                          {activeHof.team.badge_url ? (
                            <img src={activeHof.team.badge_url} alt={activeHof.team.team_name} className="w-20 h-20 md:w-24 md:h-24 object-contain" draggable={false} />
                          ) : (
                            <div className="w-full h-full" style={{ background: activeHof.team.color || "#1F2937" }} />
                          )}
                        </div>
                        <div
                          className="mt-4 font-display font-black italic uppercase tracking-tight text-2xl md:text-3xl leading-tight"
                          style={{ color: activeHof.team.color }}
                        >
                          {activeHof.team.team_name}
                        </div>
                        {activeHof.team.short_name && (
                          <div className="text-xs text-neutral-500 mt-1 uppercase tracking-widest">{activeHof.team.short_name}</div>
                        )}
                        {activeHof.team.wins != null && (
                          <div className="mt-3 text-[10px] uppercase tracking-widest text-neutral-400">
                            <span className="text-white font-bold">{activeHof.team.wins || 0}</span> Siege als Team
                          </div>
                        )}
                      </div>

                      {/* Two team drivers below */}
                      {activeHof.team.drivers && activeHof.team.drivers.length > 0 && (
                        <div
                          className="mt-6 pt-5 border-t border-white/10 grid gap-3"
                          style={{ gridTemplateColumns: `repeat(${Math.min(activeHof.team.drivers.length, 2)}, minmax(0, 1fr))` }}
                          data-testid="hof-team-drivers"
                        >
                          {activeHof.team.drivers.slice(0, 2).map((td) => (
                            <div
                              key={td.driver_id}
                              className="flex items-center gap-3 border border-white/10 bg-black/30 p-3"
                              data-testid={`hof-team-driver-${td.driver_id}`}
                            >
                              <div className="shrink-0 w-14 h-14 border border-white/10 bg-[#11151E] overflow-hidden flex items-center justify-center">
                                {td.photo_url ? (
                                  <img src={td.photo_url} alt={td.driver_name} className="w-full h-full object-cover" draggable={false} />
                                ) : (
                                  <div className="w-full h-full" style={{ background: activeHof.team.color || "#1F2937" }} />
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="font-display font-black italic uppercase tracking-tight text-base leading-tight truncate">
                                  {td.driver_name}
                                </div>
                                <div className="flex items-center gap-2 mt-1 text-[11px]">
                                  {td.country_code && (
                                    <img src={`https://flagcdn.com/w40/${td.country_code.toLowerCase()}.png`} alt="" className="w-4 h-3 object-cover border border-white/10" draggable={false} />
                                  )}
                                  {td.number > 0 && <span className="text-neutral-500">#{td.number}</span>}
                                  <span className="text-neutral-400">P{td.position}</span>
                                </div>
                              </div>
                              <div className="text-right shrink-0">
                                <div className="font-display font-black italic text-lg leading-none text-white">{td.points ?? 0}</div>
                                <div className="text-[9px] uppercase tracking-widest text-neutral-500 mt-0.5">Pkt</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>

            {championHistory.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => setHofIdx((hofSafeIdx - 1 + championHistory.length) % championHistory.length)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 z-30 w-10 h-10 flex items-center justify-center border border-amber-400/20 bg-black/40 hover:bg-amber-500/20 transition-colors"
                  data-testid="hof-slider-prev"
                  aria-label="Vorherige Saison"
                >
                  <ChevronLeft className="w-5 h-5 text-white" />
                </button>
                <button
                  type="button"
                  onClick={() => setHofIdx((hofSafeIdx + 1) % championHistory.length)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 z-30 w-10 h-10 flex items-center justify-center border border-amber-400/20 bg-black/40 hover:bg-amber-500/20 transition-colors"
                  data-testid="hof-slider-next"
                  aria-label="Nächste Saison"
                >
                  <ChevronRight className="w-5 h-5 text-white" />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2" data-testid="hof-slider-dots">
                  {championHistory.map((c, i) => (
                    <button
                      key={c.season_id}
                      type="button"
                      onClick={() => setHofIdx(i)}
                      className={`h-1.5 transition-all ${i === hofSafeIdx ? "w-8 bg-amber-400" : "w-3 bg-white/30 hover:bg-white/60"}`}
                      aria-label={`Saison ${c.season_name}`}
                      data-testid={`hof-slider-dot-${c.season_id}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* SUB RACES */}
      {subRaces.length > 0 && (
        <section className="mt-20" data-testid="home-races-section">
          <SectionHeader title="Letzte Rennberichte" link="/rennberichte" linkLabel="Alle Berichte" />
          <div className="grid md:grid-cols-3 gap-6 mt-8">
            {subRaces.map((r) => (
              <Link key={r.id} to={`/rennberichte/${r.slug}`} className="card-surface block group overflow-hidden">
                <div className="relative h-48 overflow-hidden">
                  {r.hero_image && <img src={r.hero_image} alt={r.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-transparent to-transparent" />
                </div>
                <div className="p-5">
                  <div className="eyebrow">{r.race_name}</div>
                  <div className="font-display font-black italic uppercase text-xl mt-2 leading-tight group-hover:text-[#00E5FF]">{r.title}</div>
                  <p className="text-sm text-neutral-400 mt-2 line-clamp-2">{r.summary}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* STANDINGS PREVIEW */}
      {standings.length > 0 && (
        <section className="mt-24" data-testid="home-standings-section">
          <SectionHeader title="Fahrerwertung" link="/wertung" linkLabel="Komplette Tabelle" />
          <div className="mt-8 border border-white/10">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-neutral-400 uppercase text-xs tracking-widest">
                <tr>
                  <th className="text-left px-4 py-3 w-16">Pos</th>
                  <th className="text-left px-4 py-3">Fahrer</th>
                  <th className="text-left px-4 py-3 hidden sm:table-cell">Team</th>
                  <th className="text-right px-4 py-3 w-20">Siege</th>
                  <th className="text-right px-4 py-3 w-24">Punkte</th>
                </tr>
              </thead>
              <tbody>
                {standings.slice(0, 5).map((row) => (
                  <tr key={row.driver_id} className="border-t border-white/5 hover:bg-white/5">
                    <td className="px-4 py-3 font-display font-black italic text-lg" style={{ color: posColor(row.position) }}>{row.position}</td>
                    <td className="px-4 py-3 font-bold">{row.driver_name}</td>
                    <td className="px-4 py-3 hidden sm:table-cell" style={{ color: row.team_color }}>{row.team_name}</td>
                    <td className="px-4 py-3 text-right">{row.wins}</td>
                    <td className="px-4 py-3 text-right font-bold text-[#00E5FF]">{row.points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

const SectionHeader = ({ title, link, linkLabel }) => (
  <div className="flex items-end justify-between gap-4 border-b border-white/10 pb-4">
    <div>
      <div className="h-1 w-12 bg-[#00E5FF] mb-3" />
      <h2 className="font-display font-black italic uppercase tracking-normal text-3xl sm:text-4xl leading-none">{title}</h2>
    </div>
    {link && <Link to={link} className="btn-ghost text-xs flex items-center gap-2 shrink-0">{linkLabel} <ArrowUpRight className="w-4 h-4" /></Link>}
  </div>
);

export const posColor = (p) => p === 1 ? "#FFD700" : p === 2 ? "#C0C0C0" : p === 3 ? "#CD7F32" : "#FFFFFF";
